import isArray from 'lodash/isArray';
import omit from 'lodash/omit';

import { useReactQueryState } from '../../../common/reactQuery/hooks/useReactQueryState';

import {
  FetchLifestylesCacheKey,
  FetchLifestylesFilterFields,
  FetchLifestylesFilters,
  FetchLifestylesSort
} from '../../lifestylesTypes';
import { INITIAL_LIFESTYLES_SORT } from '../../lifestylesConstants';

const defaultIgnoredFilterNames = [
  FetchLifestylesFilterFields.LIFESTYLE_PARENT_CATEGORY_IDS,
  FetchLifestylesFilterFields.LIFESTYLE_CATEGORY_ID
];

const pureInitialFilters = { nda: false, blocked: false };

const pureInitialSort = INITIAL_LIFESTYLES_SORT;

interface LifestylesFiltersCacheOptions {
  cacheKey: FetchLifestylesCacheKey;
  initialFilters?: FetchLifestylesFilters;
  initialSort?: FetchLifestylesSort;
  ignoredFilterNames?: FetchLifestylesFilterFields[];
}

function useLifestylesFiltersCache({
  cacheKey,
  initialFilters = pureInitialFilters,
  initialSort = pureInitialSort,
  ignoredFilterNames = defaultIgnoredFilterNames
}: LifestylesFiltersCacheOptions) {
  const filtersCacheKey = `${cacheKey}-filters`;
  const sortCacheKey = `${cacheKey}-sort`;

  const {
    value: cachedLifestylesFilters,
    setValue: setCachedLifestylesFilters
  } = useReactQueryState<FetchLifestylesFilters>(
    filtersCacheKey,
    initialFilters
  );

  const { value: cachedLifestylesSort, setValue: setCachedLifestylesSort } =
    useReactQueryState<FetchLifestylesSort>(sortCacheKey, initialSort);

  return {
    defaultInitialFilters: initialFilters,
    defaultInitialSort: initialSort,
    cachedLifestylesFilters,
    cachedLifestylesSort,
    changeCachedLifestylesFilters: (
      changedFilters: FetchLifestylesFilters,
      removedFilterNames: string[] = []
    ) => {
      setCachedLifestylesFilters(
        omit({ ...cachedLifestylesFilters, ...changedFilters }, [
          ...(isArray(removedFilterNames) ? removedFilterNames : []),
          ...ignoredFilterNames
        ])
      );
    },
    setCachedLifestylesFilters: (nextFilters: FetchLifestylesFilters) => {
      setCachedLifestylesFilters(omit(nextFilters, ignoredFilterNames));
    },
    setCachedLifestylesSort,
    resetCachedLifestylesFiltersAndSort: (
      onResetFilters?: (initialFilters: FetchLifestylesFilters) => void,
      onResetSort?: (initialSort: FetchLifestylesSort) => void
    ) => {
      setCachedLifestylesFilters(initialFilters);
      onResetFilters?.(initialFilters);
      setCachedLifestylesSort(initialSort);
      onResetSort?.(initialSort);
    }
  };
}

export default useLifestylesFiltersCache;
